import {
  cancelScheduledTransfer,
  CancelScheduledTransferProps,
  getScheduledTransferRequests,
  GetScheduledTransferRequestsProps,
  sendScheduledTransfer,
  SendScheduledTransferProps,
} from "@/services/scheduled-transfer-request.service";
import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { toaster } from "@/components/ui/toaster";
import { ScheduledTransferStatus } from "@/constants/entity-status";
import { ScheduledTransferDetail, ScheduledTransferEntity } from "./scheduled-transfer.schema";
import { NavigateFunction } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { AxiosError } from "axios";
import {
  getTransferRequestFilter,
  TRFRProps,
} from "../../transfer/schema/filter-obj";
import { handlePermissionError } from "@/utils/permission-check";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
}

export const clearFiltersHandler = async ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
}: ClearFiltersHandlerProps) => {
  setValue("request_date", undefined);
  setValue("account_owner", "");
  setValue("request_amount", "");
  setValue("requester", "");
  setValue("origin_account", "");
  setValue("destiny_account", "");
  setValue("estado", []);

  submitFilters?.(getValues());
  setFiltersApplied(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchScheduledTransferRequests: (filters?: any) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchScheduledTransferRequests,
  setOpenFilters,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetScheduledTransferRequestsProps> =
    getTransferRequestFilter(event as TRFRProps);

  setCurrentFilters(filterObj);

  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  await fetchScheduledTransferRequests(filterObj);

  setOpenFilters(false);
};

export interface HandleRejectRequestHandlerProps {
  event?: FieldValues;
  setLoadingRejectRequest: Dispatch<SetStateAction<boolean>>;
  request?: ScheduledTransferDetail | null;
  navigate: NavigateFunction;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  fetchPermissions: () => Promise<void>;
}
export const handleRejectRequestHandler = async ({
  setLoadingRejectRequest,
  event,
  request,
  navigate,
  setOpenRejectModal,
  fetchPermissions,
}: HandleRejectRequestHandlerProps) => {
  setLoadingRejectRequest(true);
  try {
    const objToSend: CancelScheduledTransferProps = {
      id: Number(request?.id),
      mensaje: event?.request_detail,
      idMotivo: event?.message_motive?.[0],
      esRechazo: request?.estado == ScheduledTransferStatus.ON_REVISION,
    };

    await cancelScheduledTransfer(objToSend);

    toaster.create({
      type: "success",
      title: `Solicitud ${request?.estado == ScheduledTransferStatus.SCHEDULED ? "cancelada" : "rechazada"} exitosamente.`,
    });

    setLoadingRejectRequest(false);

    navigate(AppRoutes.SCHEDULED_TRANSFER_REQUESTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo rechazar la solicitud.",
      });
    }
    throw error;
  } finally {
    setLoadingRejectRequest(false);

    setOpenRejectModal(false);
  }
};

export interface HandleExecuteScheduledTransferProps {
  setLoadingExecuteRequest: Dispatch<SetStateAction<boolean>>;
  request?: ScheduledTransferDetail | null;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const handleExecuteRequestHandler = async ({
  setLoadingExecuteRequest,
  navigate,
  request,
  fetchPermissions,
}: HandleExecuteScheduledTransferProps) => {
  setLoadingExecuteRequest(true);
  try {
    const objToSend: SendScheduledTransferProps = {
      idSolicitud: request?.id || 0,
    };

    await sendScheduledTransfer(objToSend);

    toaster.create({
      type: "success",
      title: `Solicitud realizada con éxito.`,
    });

    setLoadingExecuteRequest(false);

    navigate(AppRoutes.SCHEDULED_TRANSFER_REQUESTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo realizar la transferencia programada.",
      });
    }
    throw error;
  } finally {
    setLoadingExecuteRequest(false);
  }
};

export interface FetchScheduledTransferRequestsHandlersProps {
  showLoad?: boolean;
  setLoadingScheduledTransferRequests: Dispatch<SetStateAction<boolean>>;
  setScheduledTransferRequests: Dispatch<
    SetStateAction<{
       listado: ScheduledTransferEntity[];
          totalPages: number;
          totalRecords: number;
          pendingRequests: boolean;
    }>
  >;
  filters?: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchScheduledTransferRequestsHandler = async ({
  showLoad,
  setLoadingScheduledTransferRequests,
  setScheduledTransferRequests,
  filters,
  navigate,
  fetchPermissions,
}: FetchScheduledTransferRequestsHandlersProps) => {
  try {
    if (showLoad) setLoadingScheduledTransferRequests(true);

    const response = await getScheduledTransferRequests({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setScheduledTransferRequests(response);

    setLoadingScheduledTransferRequests(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.message ??
          "No se pudo cargar la lista de transferencias programadas.",
      });
    }
    throw error;
  }
};
